var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { testThemeSeed } from "@/tests/TestThemeSeed";
import { createSystem, defaultConfig, defineRecipe, defineSlotRecipe, } from "@chakra-ui/react";
/**
 * More info on chakra responsive design
 * https://v2.chakra-ui.com/docs/styled-system/responsive-styles
 *
 * Breakpoints
 * const breakpoints = {
 *   base: '0em', // 0px
 *   sm: '30em', // ~480px
 *   md: '48em', // ~768px
 *   lg: '62em', // ~992px
 *   xl: '80em', // ~1280px
 *   '2xl': '96em', // ~1536px
 * }
 */
export function getJobBoardTheme(config, host) {
    var _a;
    var _b, _c, _d;
    config.topBannerBackgroundColor =
        (_b = config.topBannerBackgroundColor) !== null && _b !== void 0 ? _b : "#F4F4FE";
    config.topBannerBorderColor = "lch(from ".concat(config.topBannerBackgroundColor, " calc(l - 20) c h)");
    var jobBoardWidths = {
        base: "100%",
        lg: "960px",
        xl: "1140px",
    };
    var filterCell = {
        w: "100%",
        minW: "0",
    };
    var focusStyle = {
        outline: "none",
        border: "1px solid ".concat(config.focusBorderColor),
        color: config.focusTextColor,
        boxShadow: "0 0 0 4px ".concat(config.focusBoxShadowColor),
    };
    var conditions = host
        ? {
            light: "".concat(host, " &, .light &"),
        }
        : {};
    return createSystem(defaultConfig, {
        cssVarsRoot: host,
        preflight: { scope: host },
        conditions: conditions,
        globalCss: (_a = {},
            _a[host !== null && host !== void 0 ? host : "dummy"] = (_d = (_c = defaultConfig === null || defaultConfig === void 0 ? void 0 : defaultConfig.globalCss) === null || _c === void 0 ? void 0 : _c.html) !== null && _d !== void 0 ? _d : {},
            _a[":host, body"] = {
                fontFamily: '"Open Sans", Roboto, sans-serif',
                fontSize: "14px",
                fontFeatureSettings: "normal",
                color: config.fontColor,
                backgroundColor: config.backgroundColor,
            },
            _a._focusVisible = focusStyle,
            _a),
        theme: {
            semanticTokens: {
                shadows: {
                    focus: { value: "0 0 0 4px ".concat(config.focusBoxShadowColor) },
                },
            },
            recipes: {
                // Chakra recipes additions -- Adding new variants
                button: defineRecipe({
                    base: {
                        _focusVisible: focusStyle,
                    },
                    variants: {
                        variant: {
                            ApplyButton: {
                                borderRadius: "md",
                                backgroundColor: config.applyButtonBackgroundColor,
                                color: config.applyButtonTextColor,
                                _hover: {
                                    backgroundColor: config.applyButtonBackgroundHoverColor,
                                },
                                _focus: {
                                    color: config.applyButtonTextColor,
                                    outline: "none",
                                    border: "1px solid ".concat(config.focusBorderColor),
                                    boxShadow: "0 0 0 4px ".concat(config.focusBoxShadowColor),
                                },
                                fontSize: "12px",
                                fontWeight: "400",
                                w: "112px",
                            },
                            backToLeftPanelModal: {
                                border: "1px solid ".concat(config.inputBorderColor),
                                margin: "16px",
                                size: "lg",
                                w: "calc(100% - 32px)",
                                _focusVisible: focusStyle,
                                _focus: focusStyle,
                            },
                            dropdown: {
                                borderRadius: "md",
                                border: "1px solid ".concat(config.inputBorderColor),
                                backgroundColor: config.inputBackgroundColor,
                                color: config.fontColor,
                                _hover: {
                                    _disabled: {
                                        backgroundColor: config.backgroundColor,
                                    },
                                    backgroundColor: config.dropdownBackgroundHoverColor,
                                },
                                _focus: focusStyle,
                                fontSize: "14px",
                                fontWeight: 400,
                            },
                            pagination: {
                                color: config.fontColor,
                                borderColor: config.paginationButtonBorderColor,
                                backgroundColor: config.paginationButtonBackgroundColor,
                                borderRadius: 0,
                                cursor: "pointer",
                                fontWeight: 400,
                                fontSize: "14px",
                                paddingRight: "20px",
                                paddingLeft: "20px",
                                height: "32px",
                                width: "30px",
                                "&:not(.active):hover": {
                                    backgroundColor: config.paginationButtonBackgroundHoverColor,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                },
                                "&.active": {
                                    color: config.paginationButtonTextActiveColor,
                                    backgroundColor: config.paginationButtonBackgroundActiveColor,
                                    fontWeight: 600,
                                },
                                "&.step": {
                                    fontWeight: 600,
                                },
                                _focus: focusStyle,
                            },
                        },
                    },
                }),
                badge: defineRecipe({
                    variants: {
                        variant: {
                            job: {
                                fontSize: "12px",
                                color: config.salaryBadgeTextColor,
                                textTransform: "none",
                                fontWeight: 400,
                                backgroundColor: config.salaryBadgeBackgroundColor,
                                mr: "8px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100%",
                            },
                        },
                    },
                }),
                // https://nekocalc.com/px-to-rem-converter
                text: defineRecipe({
                    variants: {
                        variant: {
                            // Details job view
                            detailsTitle: {
                                fontSize: "20px",
                                fontWeight: 600,
                                color: config.detailsTitleColor,
                                lineHeight: "26px",
                                paddingRight: "12px",
                            },
                            detailsCompany: {
                                fontSize: "14px",
                                color: config.detailsCompanyNameColor,
                                lineHeight: "26px",
                                paddingRight: "12px",
                            },
                            detailsLocation: {
                                fontSize: "14px",
                                lineHeight: "26px",
                                paddingRight: "12px",
                            },
                            postedAt: {
                                fontSize: "11px",
                                lineHeight: "normal",
                                marginTop: "10px",
                            },
                            // List view
                            listingTitle: {
                                fontSize: "15px",
                                lineHeight: "normal",
                                color: config.listItemTitleColor,
                                fontWeight: 600,
                            },
                            listingTitleMobile: {
                                fontSize: "15px",
                                lineHeight: "normal",
                                color: config.listItemTitleColor,
                                fontWeight: 600,
                                pb: "5px",
                            },
                            listingCompany: {
                                fontSize: "13px",
                                lineHeight: "normal",
                                color: config.listItemCompanyNameColor,
                            },
                            listingLocation: {
                                fontSize: "13px",
                                lineHeight: "normal",
                            },
                            // Warning
                            searchResultsWarning: {
                                fontSize: "25px",
                            },
                        },
                    },
                }),
                link: defineRecipe({
                    base: {
                        _focus: focusStyle,
                    },
                    variants: {
                        variant: {
                            topBanner: {
                                textDecoration: "underline",
                                color: "3B4045",
                                display: "inline-block",
                            },
                            header: {
                                color: config.headerTextColor,
                                verticalAlign: "middle",
                                margin: "auto",
                                fontSize: "13px",
                                paddingLeft: "22px",
                                letterSpacing: "normal",
                                _hover: {
                                    textDecoration: "underline",
                                },
                            },
                            menu: {
                                fontSize: "20px",
                                lineHeight: "26px",
                                letterSpacing: "normal",
                                color: config.fontColor,
                                _hover: {
                                    backgroundColor: config.autocompleteBackgroundHoverColor,
                                },
                                _focus: {
                                    backgroundColor: config.autocompleteBackgroundFocusColor,
                                    borderColor: config.focusBorderColor,
                                    outline: "none",
                                },
                            },
                            footer: {
                                verticalAlign: "middle",
                                m: "auto",
                                color: config.footerLinkTextColor,
                                textDecoration: "underline",
                                fontSize: "12px",
                                pl: { base: "0", lg: "22px" },
                                _focus: {
                                    color: config.footerLinkTextColor,
                                },
                            },
                            social: {
                                _focus: {
                                    color: config.footerLinkTextColor,
                                },
                            },
                        },
                    },
                }),
                separator: defineRecipe({
                    variants: {
                        variant: {
                            header: {
                                borderColor: config.headerLinksDividerColor,
                                maxH: "18px",
                                w: "1px",
                                my: "auto",
                                ml: "22px",
                            },
                        },
                    },
                }),
                ApplyButtonWrapper: defineRecipe({
                    variants: {
                        variant: {
                            searchResultsDetailsHeader: {
                                pl: { base: "0px", lg: "42px" },
                                pt: { base: "20px", lg: "14px" },
                            },
                            searchResultsDetailsFooter: {
                                pt: "26px",
                            },
                        },
                    },
                }),
                RemoteOnlyToggle: defineRecipe({
                    base: {
                        backgroundColor: config.inputBackgroundColor,
                        border: "1px solid ".concat(config.inputBorderColor),
                        borderRadius: "md",
                        color: config.fontColor,
                        fontSize: "14px",
                        fontWeight: 400,
                        px: "15px",
                        py: "9px",
                        lineHeight: "normal",
                        cursor: "pointer",
                        _focus: {
                            boxShadow: "none",
                        },
                        "& > span.chakra-switch__label": {
                            _focus: {
                                outline: "none",
                                border: "none",
                                boxShadow: "none",
                            },
                        },
                        "& > span.chakra-switch__control": {
                            backgroundColor: config.inputBorderColor,
                            _focus: {
                                // Use outlines as to not change the element size
                                border: "none",
                                outlineOffset: 0,
                                outline: "1px solid ".concat(config.focusBorderColor),
                                color: config.focusTextColor,
                                boxShadow: "0 0 0 4px ".concat(config.focusBoxShadowColor),
                            },
                        },
                        "& > span.chakra-switch__control[data-state=checked]": {
                            backgroundColor: config.toggleCheckedBackgroundColor,
                        },
                        "& > span.chakra-switch__control .chakra-switch__thumb": {
                            _focus: {
                                // Use outlines as to not change the element size
                                border: "none",
                                outlineOffset: 0,
                                outline: "1px solid ".concat(config.focusBorderColor),
                            },
                        },
                        "& > span.chakra-switch__control[data-state=checked] .chakra-switch__thumb": {
                            backgroundColor: config.backgroundColor,
                        },
                    },
                }),
                PaginationSerp: defineRecipe({
                    base: {
                        mt: "24px",
                        "& li:first-of-type > *": {
                            borderLeftRadius: "xs",
                        },
                        "& li:last-of-type > *": {
                            borderRightRadius: "xs",
                        },
                    },
                }),
                SerpJobs: defineRecipe({
                    base: {
                        ml: "0px",
                        listStyleType: "none",
                    },
                }),
                SerpJob: defineRecipe({
                    base: {
                        borderRadius: "md",
                        borderWidth: "1px",
                        marginBottom: "16px",
                        backgroundColor: config.listItemBackgroundColor,
                        _hover: {
                            cursor: "cursor",
                            backgroundColor: config.listItemBackgroundHoverColor,
                        },
                        _focus: focusStyle,
                    },
                    variants: {
                        variant: {
                            inactive: {
                                borderColor: config.listItemBorderInactiveColor,
                            },
                            active: {
                                borderColor: config.listItemBorderActiveColor,
                            },
                        },
                    },
                }),
                SearchResultsCompanyLogo: defineRecipe({
                    base: {
                        overflow: "hidden",
                        borderWidth: "1px",
                        borderColor: config.companyLogoBorderColor,
                        borderRadius: "md",
                        p: "2px",
                        w: "58px",
                        h: "58px",
                    },
                    variants: {
                        variant: {
                            details: {
                                mr: "40px",
                            },
                            mobile: {
                                w: "38px",
                                h: "38px",
                            },
                        },
                    },
                }),
                SearchResultsListItem: defineRecipe({
                    base: {
                        m: { base: "5px", lg: "20px" },
                    },
                }),
                SearchResultsListItemDetails: defineRecipe({
                    base: {
                        paddingLeft: { base: "5px", lg: "20px" },
                    },
                }),
                JobBadges: defineRecipe({
                    base: {
                        my: "3px",
                    },
                }),
                CompanyAvatar: defineRecipe({
                    base: {
                        fontSize: "20px",
                        fontWeight: 700,
                        color: config.companyLogoColor,
                        textTransform: "uppercase",
                        h: "100%",
                        borderRadius: "md",
                    },
                }),
                Header: defineRecipe({
                    base: {
                        h: { base: "50px", lg: "102px" },
                        mt: { base: "15px", lg: "26px" },
                        w: jobBoardWidths,
                        px: { base: "15px", lg: "0" },
                        maxW: "100%",
                    },
                }),
                SocialLinks: defineRecipe({
                    base: {
                        w: "17px",
                        h: "17px",
                        pl: "3px",
                    },
                }),
                ResponsiveImage: defineRecipe({
                    base: {
                        w: { base: "180px", lg: "250px" },
                    },
                }),
                JobBoard: defineRecipe({
                    base: {
                        minHeight: "100vh",
                        flexDirection: "column",
                    },
                }),
            },
            slotRecipes: {
                TwoPane: defineSlotRecipe({
                    slots: [
                        "leftPane",
                        "rightPane",
                        "twoPane",
                        "stickyTopContainer",
                        "modal",
                    ],
                    base: {
                        twoPane: {
                            w: jobBoardWidths,
                            justifyContent: "center",
                            px: { base: "0px", lg: "15px" },
                            mx: "auto",
                            maxW: jobBoardWidths,
                            pb: "104px",
                        },
                        leftPane: {
                            w: { base: "100%", lg: "370px" },
                            mt: { base: "16px", lg: "0" },
                            px: "15px",
                        },
                        rightPane: {
                            w: { base: "100%", lg: "650px" },
                            px: { base: "0px", lg: "15px" },
                            display: { base: "none", lg: "inline" },
                        },
                        modal: {
                            backgroundColor: config.backgroundColor,
                        },
                        stickyTopContainer: {
                            width: "100%",
                            height: "calc(100vh - var(--top-height))",
                            top: "var(--top-height)",
                            display: ["none", "flex"],
                            position: "sticky",
                        },
                    },
                }),
                AutocompleteInput: defineSlotRecipe({
                    slots: ["container", "input", "list", "listItem"],
                    base: {
                        container: {
                            width: "100%",
                        },
                        input: {
                            fontSize: "14px",
                            color: config.fontColor,
                            backgroundColor: config.inputBackgroundColor,
                            borderRadius: "md",
                            border: "1px solid ".concat(config.inputBorderColor),
                            _focus: {
                                outline: "none",
                                border: "1px solid ".concat(config.focusBorderColor),
                                boxShadow: "0 0 0 4px ".concat(config.focusBoxShadowColor),
                                color: config.inputTextFocusedColor,
                            },
                        },
                        list: {
                            bgColor: config.autocompleteBackgroundColor,
                            boxShadow: "md",
                            borderRadius: "md",
                            mt: { base: "2px -16px", md: "5px" },
                            padding: "6px",
                            border: "1px solid ".concat(config.inputBorderColor),
                            width: "100%",
                        },
                        listItem: {
                            position: "relative",
                            p: { base: "12px 28px", md: "12px 12px" },
                            cursor: "pointer",
                            width: "100%",
                            border: "1px solid transparent",
                            _hover: {
                                backgroundColor: config.autocompleteBackgroundHoverColor,
                            },
                            _focus: {
                                backgroundColor: config.autocompleteBackgroundFocusColor,
                                border: "1px solid ".concat(config.focusBorderColor),
                                outline: "0",
                                boxShadow: "none",
                            },
                        },
                    },
                }),
                JobFilterMultiSelect: defineSlotRecipe({
                    slots: ["root", "control", "input", "content", "item", "empty"],
                    base: {
                        root: {
                            width: "100%",
                        },
                        control: {
                            h: "40px",
                            color: config.fontColor,
                            backgroundColor: config.inputBackgroundColor,
                            borderRadius: "md",
                            border: "1px solid ".concat(config.inputBorderColor),
                            _focusWithin: {
                                outline: "none",
                                border: "1px solid ".concat(config.focusBorderColor),
                                boxShadow: "0 0 0 4px ".concat(config.focusBoxShadowColor),
                                color: config.inputTextFocusedColor,
                            },
                        },
                        input: {
                            h: "100%",
                            border: "none",
                            _focus: {
                                outline: "none",
                            },
                            fontSize: "14px",
                            color: "inherit",
                        },
                        content: {
                            color: config.fontColor,
                            backgroundColor: config.autocompleteBackgroundColor,
                            boxShadow: "md",
                            borderRadius: "md",
                            mt: "5px",
                            padding: "6px",
                            border: "1px solid ".concat(config.inputBorderColor),
                            maxHeight: "300px",
                            overflowY: "auto",
                            zIndex: "dropdown",
                        },
                        item: {
                            p: "12px",
                            cursor: "pointer",
                            border: "1px solid transparent",
                            _hover: {
                                backgroundColor: config.autocompleteBackgroundHoverColor,
                            },
                            "&[data-highlighted]": {
                                backgroundColor: config.autocompleteBackgroundFocusColor,
                                border: "1px solid ".concat(config.focusBorderColor),
                            },
                        },
                        empty: {
                            p: "12px",
                        },
                    },
                }),
                ViewJob: defineSlotRecipe({
                    slots: ["container", "body"],
                    base: {
                        container: {
                            backgroundColor: config.listItemBackgroundColor,
                            borderColor: config.detailsContentBorderColor,
                            borderWidth: "1px",
                            borderRadius: "md",
                            p: "52px",
                            mb: "10px",
                            overflow: "auto",
                            position: "sticky",
                        },
                        body: {
                            px: "0px",
                            pt: "26px",
                            lineHeight: "24px",
                            // The job description is injected as raw HTML and lives inside a
                            // shadow DOM, so the host page's styles can't cascade in. Restore
                            // typographic structure that Chakra's CSS reset strips away.
                            "& h1, & h2": {
                                fontSize: "20px",
                                fontWeight: 600,
                                lineHeight: "1.3",
                                mt: "24px",
                                mb: "8px",
                            },
                            "& h3": {
                                fontSize: "16px",
                                fontWeight: 600,
                                lineHeight: "1.3",
                                mt: "20px",
                                mb: "6px",
                            },
                            "& h4, & h5, & h6": {
                                fontSize: "14px",
                                fontWeight: 600,
                                lineHeight: "1.3",
                                mt: "16px",
                                mb: "4px",
                            },
                            "& p": {
                                mb: "12px",
                            },
                            "& ul, & ol": {
                                pl: "24px",
                                mb: "12px",
                            },
                            "& li": {
                                display: "list-item",
                                mb: "4px",
                            },
                            // Chakra's reset sets `li { list-style: none }`, so re-assert the
                            // marker on the list items themselves rather than the container.
                            "& ul, & ul li": {
                                listStyleType: "disc",
                            },
                            "& ol, & ol li": {
                                listStyleType: "decimal",
                            },
                            "& b, & strong": {
                                fontWeight: 600,
                            },
                            // First heading/element shouldn't push the whole body down.
                            "& > :first-child": {
                                mt: "0px",
                            },
                        },
                    },
                }),
                SearchFormChildren: defineSlotRecipe({
                    slots: [
                        "what",
                        "where",
                        "submit",
                        "container",
                        "searchContainer",
                        "filterContainer",
                        "remoteOnlyToggle",
                        "countryDropdown",
                        "search",
                        "filter",
                        "jobCategoryFilter",
                        "dateFilter",
                        "unavailableDateFilter",
                        "jobTypeFilter",
                    ],
                    base: {
                        container: {
                            display: "grid",
                            w: "100%",
                            alignItems: "start",
                            // apply to all children elements
                            "& > *": {
                                py: { base: "5px" },
                                px: { base: "0", lg: "15px" },
                                mb: { base: "5px" },
                                pb: { base: "1px" },
                            },
                        },
                        searchContainer: {
                            gridTemplateColumns: {
                                base: "minmax(0, 1fr)",
                                lg: "minmax(0, 48fr) minmax(0, 34fr) minmax(0, 27.5fr)",
                            },
                        },
                        filterContainer: {
                            display: "grid",
                            gridTemplateColumns: {
                                base: "minmax(0, 1fr)",
                                md: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(3, minmax(0, 1fr))",
                            },
                            w: "100%",
                            gap: "2",
                            "& > *": {
                                px: "0",
                                py: "0",
                                mb: "0",
                                pb: "0",
                            },
                        },
                        // form-controls
                        what: {
                            w: "100%",
                            minW: "0",
                        },
                        where: {
                            w: "100%",
                            minW: "0",
                        },
                        remoteOnlyToggle: __assign(__assign({}, filterCell), { whiteSpace: "nowrap", "& > label": {
                                w: "100%",
                            } }),
                        submit: {
                            w: "100%",
                            minW: "0",
                        },
                        // Should match the width of the what
                        countryDropdown: __assign(__assign({}, filterCell), { "& > button": {
                                w: "100%",
                            } }),
                        filter: __assign({}, filterCell),
                        dateFilter: __assign({}, filterCell),
                        unavailableDateFilter: {
                            w: "100%",
                        },
                        jobCategoryFilter: __assign({}, filterCell),
                        jobTypeFilter: __assign({}, filterCell),
                        search: {
                            borderRadius: "md",
                            backgroundColor: config.searchButtonBackgroundColor,
                            color: config.searchButtonTextColor,
                            _hover: {
                                backgroundColor: config.searchButtonBackgroundHoverColor,
                            },
                            _focus: {
                                color: config.searchButtonTextColor,
                                outline: "none",
                                border: "1px solid ".concat(config.focusBorderColor),
                                boxShadow: "0 0 0 4px ".concat(config.focusBoxShadowColor),
                            },
                            fontSize: "14px",
                            fontWeight: 400,
                            w: "100%",
                        },
                    },
                }),
                Dropdown: defineSlotRecipe({
                    slots: ["menuContent", "menuItemOption"],
                    base: {
                        menuContent: {
                            borderRadius: "md",
                            border: "1px solid ".concat(config.inputBorderColor),
                            width: "100%",
                        },
                        menuItemOption: {
                            fontSize: "14px",
                            color: config.fontColor,
                            border: "1px solid transparent",
                            _hover: {
                                backgroundColor: config.autocompleteBackgroundHoverColor,
                            },
                            _focus: {
                                border: "1px solid transparent",
                                boxShadow: "none",
                                backgroundColor: "inherit",
                            },
                            "&[data-highlighted]": {
                                backgroundColor: "".concat(config.autocompleteBackgroundFocusColor, " !important"),
                                border: "1px solid ".concat(config.focusBorderColor, " !important"),
                            },
                        },
                    },
                }),
                Top: defineSlotRecipe({
                    slots: [
                        "stickyTop",
                        "searchFormBackgroundWrapper",
                        "searchFormWrapper",
                    ],
                    base: {
                        stickyTop: {
                            position: { base: "static", lg: "sticky" },
                            top: 0,
                            w: "100%",
                            zIndex: 100,
                            opacity: 1,
                            bg: config.backgroundColor,
                        },
                        searchFormBackgroundWrapper: {
                            bgGradient: config.searchFormBackgroundGradient,
                            py: config.searchFormBackgroundGradient
                                ? { base: "0", lg: "24px" }
                                : undefined,
                            mb: { base: "4px", lg: "24px" },
                        },
                        searchFormWrapper: {
                            w: jobBoardWidths,
                            py: config.searchFormBackgroundGradient
                                ? { base: "6px", lg: "14px" }
                                : undefined,
                            px: "15px",
                            bg: config.searchFormBackgroundColor,
                            borderRadius: { base: 0, lg: "md" },
                        },
                    },
                }),
                TopBanner: defineSlotRecipe({
                    slots: ["wrapper", "closeBtn"],
                    base: {
                        wrapper: {
                            bg: config.topBannerBackgroundColor,
                            border: "1px solid ".concat(config.topBannerBorderColor),
                            px: "45px",
                            py: "8px",
                            display: { base: "none", lg: "flex" },
                            color: "#3B4045",
                            fontSize: "13px",
                        },
                        closeBtn: {
                            bg: config.topBannerBackgroundColor,
                            color: "#3B4045",
                            cursor: "pointer",
                            ml: "5px",
                            width: "13px",
                            height: "13px",
                            _hover: {
                                bg: config.topBannerBackgroundColor,
                                color: "#3B4045",
                            },
                        },
                    },
                }),
                Footer: defineSlotRecipe({
                    slots: ["wrapper", "content"],
                    base: {
                        wrapper: {
                            height: "auto",
                            backgroundColor: config.footerBackgroundColor,
                            marginTop: "auto",
                        },
                        content: {
                            color: config.footerContentColor,
                            py: "28px",
                            fontSize: "12px",
                            w: jobBoardWidths,
                            lineHeight: "28px",
                            px: "15px",
                        },
                    },
                }),
            },
        },
    });
}
// Dummy export, for type generation
export default getJobBoardTheme(testThemeSeed);
